import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Send, Upload, CheckCircle2 } from 'lucide-react'
import { careersApi } from '../../services/api'
import Button from '../ui/Button'
import Card from '../ui/Card'

const INITIAL = {
  full_name: '',
  email: '',
  phone: '',
  department_id: '',
  specialization_id: '',
}

export default function JobApplicationForm({ departments = [], forceEnglish = false }) {
  const { t, i18n } = useTranslation()
  const copy = forceEnglish ? i18n.getFixedT('en') : t
  const [form, setForm] = useState(INITIAL)
  const [cv, setCv] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(null)
  const [error, setError] = useState(null)

  const selectedDepartment = departments.find((dept) => String(dept.id) === String(form.department_id))
  const specializations = (selectedDepartment?.specializations || []).filter((item) => item.is_active !== false)

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'department_id') {
      setForm((prev) => ({ ...prev, department_id: value, specialization_id: '' }))
      return
    }
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!cv) {
      setError(copy('careers.form.cvRequired'))
      return
    }
    if (!form.department_id) {
      setError(copy('careers.form.departmentRequired'))
      return
    }
    if (specializations.length > 0 && !form.specialization_id) {
      setError(copy('careers.form.specializationRequired'))
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('full_name', form.full_name)
      formData.append('email', form.email)
      formData.append('phone', form.phone)
      formData.append('department_id', form.department_id)
      if (form.specialization_id) {
        formData.append('specialization_id', form.specialization_id)
      }
      formData.append('cv', cv)

      const { data } = await careersApi.apply(formData)
      setSuccess(data.data?.reference_number || data.reference_number)
      setForm(INITIAL)
      setCv(null)
    } catch {
      setError(copy('careers.form.error'))
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <Card className="border-green-200 bg-green-50 p-8 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-600" />
        <h3 className="text-xl font-bold text-primary">{copy('careers.form.successTitle')}</h3>
        <p className="mt-2 text-text/70">{copy('careers.form.successText')}</p>
        <p className="mt-4 text-lg font-semibold text-accent">{success}</p>
      </Card>
    )
  }

  const localizedName = (item) =>
    forceEnglish || i18n.language !== 'ar'
      ? item.name_en || item.name_ar
      : item.name_ar || item.name_en

  return (
    <Card id="apply" className="p-6 sm:p-8">
      <h2 className="mb-2 text-2xl font-bold text-primary">{copy('careers.form.title')}</h2>
      <p className="mb-6 text-sm text-text/70">{copy('careers.form.subtitle')}</p>

      <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="full_name" className="mb-1 block text-sm font-medium text-text/80">
            {copy('careers.form.full_name')} *
          </label>
          <input
            id="full_name"
            type="text"
            name="full_name"
            value={form.full_name}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-text/80">
            {copy('careers.form.email')} *
          </label>
          <input
            id="email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <div>
          <label htmlFor="phone" className="mb-1 block text-sm font-medium text-text/80">
            {copy('careers.form.phone')} *
          </label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <div>
          <label htmlFor="department_id" className="mb-1 block text-sm font-medium text-text/80">
            {copy('careers.form.department')} *
          </label>
          <select
            id="department_id"
            name="department_id"
            value={form.department_id}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">{copy('careers.form.selectDepartment')}</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {localizedName(dept)}
              </option>
            ))}
          </select>
        </div>

        {specializations.length > 0 ? (
          <div className="sm:col-span-2">
            <label htmlFor="specialization_id" className="mb-1 block text-sm font-medium text-text/80">
              {copy('careers.form.specialization')} *
            </label>
            <select
              id="specialization_id"
              name="specialization_id"
              value={form.specialization_id}
              onChange={handleChange}
              required
              className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">{copy('careers.form.selectSpecialization')}</option>
              {specializations.map((item) => (
                <option key={item.id} value={item.id}>
                  {localizedName(item)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-text/80">
            {copy('careers.form.cv')} *
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-border px-4 py-3 text-sm text-text/70 hover:border-primary">
            <Upload className="h-4 w-4" />
            {cv ? cv.name : copy('careers.form.uploadCv')}
            <input
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={(e) => setCv(e.target.files?.[0] || null)}
            />
          </label>
          <p className="mt-1 text-xs text-text/50">{copy('careers.form.cvHint')}</p>
        </div>

        {error && <p className="sm:col-span-2 text-sm text-red-600">{error}</p>}

        <div className="sm:col-span-2">
          <Button type="submit" variant="accent" size="lg" disabled={submitting} className="w-full sm:w-auto">
            <Send className="h-4 w-4" />
            {submitting ? copy('careers.form.submitting') : copy('careers.form.submit')}
          </Button>
        </div>
      </form>
    </Card>
  )
}
